<template>
    <v-hover v-slot:default="{ hover }" class="border">
        <Lazy
            :key="`lazy-${document.id}`"
            :unrender="isGrouped && !isGroupExpanded"
            :disableLazy="!isGrouped"
            :tag="'tbody'"
        >
            <tr :class="getBackgroundColor(hover)">
                <td class="pa-1">
                    <v-tooltip top z-index="1000">
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                x-small
                                v-on="on"
                                @click="onCheckBoxSelectionChange"
                                class="ma-0 justify-center align-center"
                            >
                                <v-icon x-small>{{ checkSelectIcon }}</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t("Select") }}</span>
                    </v-tooltip>
                </td>
                <td
                    v-for="field in defaultVisibleFields"
                    v-bind:key="field.code"
                    class="py-2 px-1 text-body-2 cursor-pointer"
                    @click="onFieldsClick"
                >
                    {{ field.value }}
                </td>
                <td class="py-1 px-1 text-no-wrap" style="width: 80px;">
                    <DocumentTableActions
                        @onViewMetadataClick="onViewMetadataClick"
                        @on-version-removed="onVersionRemoved"
                        @on-document-blocked="onDocumentBlocked"
                        @on-reception-confirmed="onReceptionConfirmed"
                        @on-document-removed="onDocumentRemoved"
                        @on-flow-diagram-click="onFlowDiagramClick"
                        @on-action-click="onActionClick"
                        @on-sign-click="onSignClick"
                        @on-load-plugin-click="onloadPluginClick"
                        @on-edit-click="onEditClick"
                        @on-retry-process="onRetryProcess"
                        :document="document"
                        :totalDefaultVisibleFields="totalDefaultVisibleFields"
                    />
                </td>
            </tr>
            <tr class="row-extra_actions" v-if="showExtraActions">
                <td :colspan="totalDefaultVisibleFields + 2" class="pa-0" style="position: relative;">
                    <div class="overflow-x-auto position-relative" :style="{ maxWidth: visibleWidth + 'px' }">
                    <v-container fluid class="pa-4 grey lighten-5">
                        <v-row no-gutters>
                            <v-col cols="12">
                                <CommentList
                                    v-if="showComments"
                                    :permissions="document.permissions"
                                    :documentId="document.id"
                                    @on-close-click="onActionClick('comment')"
                                    @on-comments-updated="onCommentsUpdated"
                                />

                                <AlertList
                                    v-if="showAlerts"
                                    :documentId="document.id"
                                    @on-close-click="onActionClick('alert')"
                                    @on-alert-updated="onAlertUpdated"
                                />

                                <LinkedDocumentsList
                                    v-if="showLinkedDocuments"
                                    :documentId="document.id"
                                    @on-close-click="onActionClick('linkedDocuments')"
                                    @on-linked-documents-updated="onLinkedDocumentsUpdated"
                                />

                                <VersionList
                                    v-if="showVersions"
                                    :documentId="document.id"
                                    @on-close-click="onActionClick('version')"
                                    @on-version-click="onVersionClick"
                                />

                                <HistoryList
                                    v-if="showHistory"
                                    :documentId="document.id"
                                    @on-close-click="onActionClick('history')"
                                />

                                <AttachmentList
                                    v-if="showAttachments"
                                    :permissions="document.permissions"
                                    :documentId="document.id"
                                    :contentTypeId="document.contentTypeID"
                                    @on-close-click="onActionClick('attachment')"
                                    @on-attachment-updated="onAttachmentsUpdated"
                                />

                                <DistributionList
                                    v-if="showDistributions"
                                    :documentId="document.id"
                                    @on-close-click="onActionClick('distribution')"
                                    @on-distribution-ended="onDistributionEnded"
                                />

                                <SignatureList
                                    v-if="showSignatures"
                                    :documentId="document.id"
                                    @on-close-click="onActionClick('signatures')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                    </div>
                </td>
            </tr>
            <tr v-if="viewAllFields">
                <td :colspan="totalDefaultVisibleFields + 1" class="px-5 py-2 grey lighten-5">
                    <v-container fluid class="pa-0">
                        <v-row no-gutters>
                            <v-col cols="12">
                                <v-list dense class="pa-0 grey lighten-5">
                                    <v-list-item
                                        v-for="field in hiddenFields"
                                        :key="field.defFieldID"
                                        class="pa-0 min-height-0"
                                    >
                                        <v-list-item-content class="py-1">
                                            <v-row class="px-0 ma-0 text-body-2">
                                                <v-col class="pa-0 ma-0 font-weight-medium" cols="3">{{ field.title }}:</v-col>
                                                <v-col class="pa-0 ma-0">{{ field.value }}</v-col>
                                            </v-row>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-col>
                        </v-row>
                        <v-divider></v-divider>
                        <v-row no-gutters class="mt-2">
                            <v-col cols="6" class="text-caption">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <span v-on="on" class="d-inline-flex align-center">
                                            <v-icon x-small v-if="document.isLastVersion" class="mr-1">mdi-flag</v-icon>
                                            {{ document.version }}
                                        </span>
                                    </template>
                                    <span>{{ $t("DocumentVersion") }}</span>
                                </v-tooltip>
                            </v-col>
                            <v-col cols="6" class="text-right text-caption font-weight-medium">
                                <v-icon x-small class="mb-1">mdi-book-information-variant</v-icon>
                                {{ document.extension }}
                                <v-tooltip top z-index="1000">
                                    <template v-slot:activator="{ on }">
                                        <span class="mx-1" v-on="on">- {{ document.contentType }}</span>
                                    </template>
                                    <span>{{ $t("ContentTypeVersion") }}</span>
                                </v-tooltip>
                                <v-tooltip top z-index="1000" v-if="isPdf">
                                    <template v-slot:activator="{ on }">
                                    <v-icon v-on="on" @click="onActionClick('signatures')"
                                        >mdi-certificate</v-icon
                                    >
                                    </template>
                                    <span>{{ $t("CertifiedSignatures") }}</span>
                                </v-tooltip>
                            </v-col>
                        </v-row>
                    </v-container>
                </td>
                <td class="actions-fill-width"></td>
            </tr>
        </Lazy>
    </v-hover>
</template>

<script>
import DocumentItemMixin from '@/mixins/SearchResultsView/DocumentItemMixin';
import DocumentTableActions from './DocumentTableActions.vue';
import CommentList from '@/components/search/results/list/item/actions/comment/CommentList';
import VersionList from '@/components/search/results/list/item/actions/version/VersionList';
import HistoryList from '@/components/search/results/list/item/actions/history/HistoryList';
import AlertList from '@/components/search/results/list/item/actions/alert/AlertList';
import AttachmentList from '@/components/search/results/list/item/actions/attachment/AttachmentList';
import DistributionList from '@/components/search/results/list/item/actions/distribution/DistributionList';
import SignatureList from '@/components/search/results/list/item/actions/signature/SignatureList';
import LinkedDocumentsList from '@/components/search/results/list/item/actions/link/LinkedDocumentsList';
import Lazy from '@/components/search/results/list/Lazy.vue';

export default {
    name: 'DocumentTableRow',
    components: {
        DocumentTableActions,
        CommentList,
        VersionList,
        HistoryList,
        AlertList,
        AttachmentList,
        DistributionList,
        SignatureList,
        LinkedDocumentsList,
        Lazy
    },
    mixins: [DocumentItemMixin],
    props: {
        totalDefaultVisibleFields: Number,
        headerFields: {
            type: Array,
            required: true
        },
        isGroupExpanded: {
            type: Boolean,
            default: false
        },
        isGrouped: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            viewAllFields: false,
            visibleWidth: '100%',
            resizeObserver: null
        };
    },
    mounted() {
        this.setupResizeObserver();
    },

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    computed: {
        checkSelectIcon() {
            return this.checkBoxSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline';
        },
        checkBoxSelected() {
            return this.selectedDocuments.includes(this.document.id);
        },
        defaultVisibleFields() {
            return this.headerFields.map(headerField => {
                const match = this.document.fields.find(
                    f => f.code === headerField.Code && f.isVisible
                );

                return { code: headerField.Code, value: match ? match.value : '' };
            });
        },
        hiddenFields() {
            const visibleCodes = this.defaultVisibleFields.map(f => f.code);
            return this.document.fields.filter(field => !visibleCodes.includes(field.code) && field.isVisible);
        },
        showExtraActions() {
            return (
                this.showComments ||
                this.showAlerts ||
                this.showLinkedDocuments ||
                this.showVersions ||
                this.showHistory ||
                this.showAttachments ||
                this.showDistributions ||
                this.showSignatures
            );
        },
    },
    methods: {
        onCheckBoxSelectionChange() {
            this.onSelectionChange(!this.checkBoxSelected);
        },
        onViewMetadataClick() {
            this.viewAllFields = !this.viewAllFields;
        },
        onSignatureClick() {
            this.$emit('on-action-click', 'signatures');
        },
        setupResizeObserver() {
            const container = this.$el.closest('.document-table-container');
            if (container) {
                this.resizeObserver = new ResizeObserver(entries => {
                    for (const entry of entries) {
                        this.visibleWidth = entry.contentRect.width;
                    }
                });
                this.resizeObserver.observe(container);
                this.visibleWidth = container.clientWidth;
            }
        },
        isPdf() {
            return this.document.extension.toLowerCase() == '.pdf';
        },
    },
    watch: {
        headerFields: {
            handler() {
                this.viewAllFields = false;
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.cursor-pointer {
    cursor: pointer;
}

.min-height-0 {
    min-height: 0 !important;
}

.border {
    border-bottom: 1px solid #e0e0e0;
}
</style>